// Enhanced URL generation utilities for custom printed products
// Handles the new URL structure: /custom-printed-{category}/{brand}-{style}/{color}-{size}

/**
 * Check if enhanced URLs should be used for current page
 * @returns {boolean}
 */
export const shouldUseEnhancedUrls = () => {
  const path = window.location.pathname;
  // Check if current URL is already using enhanced URL structure (category/brand+style/color)
  const result = path.match(/\/custom-printed-[a-z0-9-]+\/[^/]+\/(?:[^/?#]+)(?:\?.*)?$/i);
  return !!result;
};

/**
 * Extract product information from current URL
 * @returns {Object|null} Product info with category and brandStyle
 */
export const getProductInfo = () => {
  const path = window.location.pathname;
  const matches = path.match(/\/custom-printed-([a-z0-9-]+)\/([^/]+)/i);
  if (matches) {
    return {
      category: matches[1],
      brandStyle: matches[2]
    };
  }
  return null;
};

/**
 * Create a URL-safe slug consistent with Rails `parameterize`
 * - lowercases
 * - removes diacritics
 * - strips apostrophes entirely (Ruby parameterize drops them before dashing,
 *   so "Men's Black" -> "mens-black", not "men-s-black")
 * - replaces non-alphanumerics with dashes
 * - collapses consecutive dashes and trims edges
 * @param {string} value
 * @returns {string}
 */
export const parameterizeColor = value => {
  if (!value) return "";
  const withoutDiacritics = value
    .toString()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "");
  return withoutDiacritics
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-+/g, "-");
};

/**
 * Extract current color from URL (simplified for color-only URLs)
 * If multiple colors are present (comma-separated)
 * @returns {Object} Current color
 */
export const getCurrentColor = () => {
  const path = window.location.pathname + window.location.search;
  const matches = path.match(/\/custom-printed-[a-z0-9-]+\/[^/]+\/([^/?#]+)(?:\?.*)?$/i);
  if (matches) {
    const colorParam = matches[1];
    const firstSlug = colorParam.split(",")[0] || "";
    return {
      color: firstSlug.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())
    };
  }
  return { color: null };
};

/**
 * Generate enhanced URL with color only (supports single or multiple colors)
 * @param {string} colorName - The color name(s)
 * @param {boolean} preserveQueryParams - Whether to preserve existing query parameters
 * @returns {string} Enhanced URL path with optional query parameters
 */
export const generateEnhancedUrl = (colorName, preserveQueryParams = false) => {
  const productInfo = getProductInfo();
  if (!productInfo) {
    return window.location.pathname; // fallback to current path
  }

  // Support multiple colors separated by '/', ',', or ' - '
  const parts = (colorName || "")
    .split(/[/,]|\s-\s/)
    .map(s => s.trim())
    .filter(Boolean);
  const color = parts.map(parameterizeColor).join(",");
  const { category, brandStyle } = productInfo;

  let enhancedPath = `/custom-printed-${category}/${brandStyle}/${color}`;

  // Preserve existing query parameters if requested
  if (preserveQueryParams) {
    const currentParams = new URLSearchParams(window.location.search);
    // Remove 'ac' parameter since it's now in the URL path
    currentParams.delete("ac");

    if (currentParams.toString()) {
      enhancedPath += `?${currentParams.toString()}`;
    }
  }

  return enhancedPath;
};

/**
 * Update URL when color changes (color-only, simplified)
 * @param {string} colorName - The new color name
 */
export const updateUrlForColorChange = colorName => {
  if (!shouldUseEnhancedUrls()) return;

  // Generate enhanced URL while preserving existing query parameters
  const enhancedPath = generateEnhancedUrl(colorName, true);

  if (window.location.pathname + window.location.search !== enhancedPath) {
    window.history.replaceState(null, null, enhancedPath);
  }
};

/**
 * PIP-3360: extract the enhanced PRODUCT BASE path (/custom-printed-{category}/{style})
 * from any enhanced URL form — the style base `.html`, a /{style}/{color} path, or the
 * bare base. Returns null for non-enhanced paths. Used to recognise that a base
 * `.html#color` swatch href and the current /{style}/{color} page belong to the same
 * product, so a color click stays an in-place selection instead of a full navigation.
 * @param {string} pathname
 * @returns {string|null}
 */
export const getEnhancedProductBasePath = pathname => {
  const matches = (pathname || "").match(
    /^(\/custom-printed-[a-z0-9-]+\/[^/?#]+?)(?:\.html)?(?:\/[^/?#]+)?$/i
  );
  return matches ? matches[1] : null;
};

/**
 * Get current color from active UI element
 * @returns {string|null} Current active color name
 */
export const getCurrentActiveColor = () => {
  const activeColorElement = document.querySelector(".js-current-color-name");
  return activeColorElement ? activeColorElement.textContent.trim() : null;
};
